/**
 * Locale utilities: route-derived language and localized path helpers.
 *
 * This module is the core fix for the SSG bug. Language is now derived from the
 * route pathname, not from global i18n or localStorage state, so the SSG pass
 * can create separate instances per locale and emit correct lang="ar"/"en" HTML.
 */

import type { Locale } from "@/content/types";

export type { Locale };

/**
 * Extract locale from pathname.
 *
 * Routes are structured as /en/... and /ar/..., so extract the first segment.
 * Falls back to 'en' for unrecognized patterns (e.g. old URLs without a locale).
 */
export function localeFromPathname(pathname: string): Locale {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (first === "en" || first === "ar") {
    return first as Locale;
  }
  // Fallback: treat as English. This will catch old URLs without a locale prefix.
  return "en";
}

/**
 * Build a localized path: /en/foo or /ar/foo.
 *
 * If `path` already starts with a locale prefix, strip it and replace.
 * This ensures switching locales on /en/services/implants consistently produces /ar/services/implants.
 */
export function localizedPath(locale: Locale, path: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const withoutLocale = normalizedPath.replace(/^\/(en|ar)/, "");
  const trimmedPath = withoutLocale === "" ? "/" : withoutLocale;
  return `/${locale}${trimmedPath}`;
}

/**
 * Text direction per locale.
 */
export const DIR: Record<Locale, "ltr" | "rtl"> = {
  en: "ltr",
  ar: "rtl",
};

/**
 * og:locale values per locale for social media metadata.
 */
export const OG_LOCALE: Record<Locale, "en_US" | "ar_EG"> = {
  en: "en_US",
  ar: "ar_EG",
};

/**
 * HTML lang attribute value (includes region for ar).
 */
export const HTML_LANG: Record<Locale, string> = {
  en: "en",
  ar: "ar",
};
